import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

import { AccountUsersService } from '../api'
import type { AccountUsersResponse, CreateUserRequest, ErrorResponse, GeneralUser, BulkCreateUsersRequest, BulkCreateUsersResponse, BulkValidateUsersResponse } from '../api'

interface AccountUsersState {
    data: AccountUsersResponse | null
    loading: boolean
    error: string | null
}

const initialState: AccountUsersState = {
    data: null,
    loading: false,
    error: null,
}

// アカウントユーザー情報を取得
const fetchAccountUsers = createAsyncThunk(
    'accountUsers/fetch',
    async (accountId: number) => {
        try {
            const request = {
                accountId,
            }
            console.log('ユーザ管理情報取得API リクエスト:', request)

            const response = await AccountUsersService.getAccountUsers(request)
            console.log('ユーザ管理情報取得API レスポンス:', response)
            console.log('アカウント情報:', response.account)
            console.log('ユーザー一覧:', response.users)
            return response
        } catch (error: unknown) {
            console.error('ユーザ管理情報取得API エラー:', error)
            if (error && typeof error === 'object' && 'body' in error) {
                const errorData = (error as { body: ErrorResponse }).body
                throw new Error(errorData.error_message || 'データ取得に失敗しました')
            }
            throw new Error('通信エラーが発生しました')
        }
    }
)

// 新規ユーザー作成
const createUser = createAsyncThunk(
    'accountUsers/createUser',
    async (userData: CreateUserRequest) => {
        try {
            const request = {
                requestBody: userData
            }
            console.log('ユーザー作成API リクエスト:', request)

            const response = await AccountUsersService.createUser(request)
            console.log('ユーザー作成API レスポンス:', response)
            return response
        } catch (error: unknown) {
            console.error('ユーザー作成API エラー:', error)
            if (error && typeof error === 'object' && 'body' in error) {
                const errorBody = (error as { body: ErrorResponse }).body
                if (errorBody.error_message) {
                    throw new Error(errorBody.error_message)
                }
            }
            throw new Error('通信エラーが発生しました。\n\nインターネット接続をご確認の上、時間をおいて再度お試しください。')
        }
    }
)

// 一括ユーザーバリデーション
const bulkValidateUsers = createAsyncThunk(
    'accountUsers/bulkValidateUsers',
    async (userData: BulkCreateUsersRequest): Promise<BulkValidateUsersResponse> => {
        try {
            const request = {
                requestBody: userData
            }
            console.log('一括ユーザーバリデーションAPI リクエスト:', request)

            const response = await AccountUsersService.bulkValidateUsers(request)
            console.log('一括ユーザーバリデーションAPI レスポンス:', response)
            return response
        } catch (error: unknown) {
            console.error('一括ユーザーバリデーションAPI エラー:', error)
            if (error && typeof error === 'object' && 'body' in error) {
                const errorBody = (error as { body: ErrorResponse }).body
                if (errorBody.error_message) {
                    throw new Error(errorBody.error_message)
                }
            }
            throw new Error('通信エラーが発生しました。\n\nインターネット接続をご確認の上、時間をおいて再度お試しください。')
        }
    }
)

// 一括ユーザー作成エラーレスポンスの型
interface BulkCreateUsersErrorResponse {
    created_users?: GeneralUser[]
    failed_user?: {
        row_index: number
        user_name: string
        error: string
    }
}

// rejectWithValue用のエラーペイロード型
interface BulkCreateUsersRejectPayload {
    created_users: GeneralUser[]
    failed_user: {
        row_index: number
        user_name: string
        error: string
    }
}

// 一括ユーザー作成（バリデーション済み前提、少数ずつ呼び出す）
const bulkCreateUsers = createAsyncThunk<
    BulkCreateUsersResponse,
    BulkCreateUsersRequest,
    { rejectValue: BulkCreateUsersRejectPayload }
>(
    'accountUsers/bulkCreateUsers',
    async (userData, { rejectWithValue }) => {
        try {
            const request = {
                requestBody: userData
            }
            console.log('一括ユーザー作成API リクエスト:', request)

            const response = await AccountUsersService.bulkCreateUsers(request)
            console.log('一括ユーザー作成API レスポンス:', response)
            return response
        } catch (error: unknown) {
            console.error('一括ユーザー作成API エラー:', error)
            if (error && typeof error === 'object' && 'body' in error) {
                const errorBody = (error as { body: BulkCreateUsersErrorResponse }).body
                // failed_userとcreated_usersをrejectWithValueで返す
                if (errorBody && errorBody.failed_user) {
                    return rejectWithValue({
                        created_users: errorBody.created_users || [],
                        failed_user: errorBody.failed_user
                    })
                }
                // ErrorResponse形式のエラーメッセージを処理
                if (errorBody && 'error_message' in errorBody) {
                    throw new Error((errorBody as { error_message: string }).error_message)
                }
            }
            throw new Error('通信エラーが発生しました。\n\nインターネット接続をご確認の上、時間をおいて再度お試しください。')
        }
    }
)

// 差分抽出ヘルパー関数
function extractDiff(current: GeneralUser, updated: GeneralUser): Partial<GeneralUser> {
    const diff: Partial<GeneralUser> = {
        account_id: updated.account_id,
        user_id: updated.user_id
    }

    // 各フィールドを比較して差分があるものだけ追加
    const fields: Array<keyof GeneralUser> = [
        'user_name', 'full_name', 'email', 'role_type', 'credit_limit'
    ]

    fields.forEach(field => {
        if (current[field] !== updated[field]) {
            diff[field] = updated[field] as never
        }
    })

    // パスワードが指定されている場合は必ず含める（差分チェック不要）
    if (updated.password) {
        diff.password = updated.password
    }

    return diff
}

// ユーザー情報更新
const updateUser = createAsyncThunk(
    'accountUsers/updateUser',
    async ({ current, updated }: { current: GeneralUser; updated: GeneralUser }) => {
        try {
            // 差分のみを抽出
            const diff = extractDiff(current, updated)

            const request = {
                requestBody: diff as GeneralUser
            }
            console.log('ユーザー更新API リクエスト:', request)

            const response = await AccountUsersService.updateUser(request)
            console.log('ユーザー更新API レスポンス:', response)
            return response as GeneralUser
        } catch (error: unknown) {
            console.error('ユーザー更新API エラー:', error)
            if (error && typeof error === 'object' && 'body' in error) {
                const errorBody = (error as { body: ErrorResponse }).body
                if (errorBody.error_message) {
                    throw new Error(errorBody.error_message)
                }
            }
            throw new Error('通信エラーが発生しました。\n\nインターネット接続をご確認の上、時間をおいて再度お試しください。')
        }
    }
)

// ユーザー削除
const deleteUser = createAsyncThunk(
    'accountUsers/deleteUser',
    async (params: { account_id: number; user_id: number }) => {
        try {
            const request = {
                requestBody: {
                    account_id: params.account_id,
                    user_id: params.user_id
                }
            }
            console.log('ユーザー削除API リクエスト:', request)

            const response = await AccountUsersService.deleteUser(request)
            console.log('ユーザー削除API レスポンス:', response)
            return response
        } catch (error: unknown) {
            console.error('ユーザー削除API エラー:', error)
            if (error && typeof error === 'object' && 'body' in error) {
                const errorBody = (error as { body: ErrorResponse }).body
                if (errorBody.error_message) {
                    throw new Error(errorBody.error_message)
                }
            }
            throw new Error('通信エラーが発生しました。\n\nインターネット接続をご確認の上、時間をおいて再度お試しください。')
        }
    }
)

// ユーザーの個別上限一括更新
const bulkUpdateUserLimits = createAsyncThunk(
    'accountUsers/bulkUpdateUserLimits',
    async (params: { account_id: number; user_limits: Array<{ user_id: number; credit_limit: number | null }> }) => {
        try {
            const request = {
                requestBody: {
                    account_id: params.account_id,
                    user_limits: params.user_limits
                }
            }
            console.log('ユーザー上限一括更新API リクエスト:', request)

            const response = await AccountUsersService.bulkUpdateUserLimits(request)
            console.log('ユーザー上限一括更新API レスポンス:', response)
            return response
        } catch (error: unknown) {
            console.error('ユーザー上限一括更新API エラー:', error)
            if (error && typeof error === 'object' && 'body' in error) {
                const errorBody = (error as { body: ErrorResponse }).body
                if (errorBody.error_message) {
                    throw new Error(errorBody.error_message)
                }
            }
            throw new Error('通信エラーが発生しました。\n\nインターネット接続をご確認の上、時間をおいて再度お試しください。')
        }
    }
)

// 指定期間のユーザーごとの使用回数と個別上限を取得
const getHistoricalUsage = createAsyncThunk(
    'accountUsers/getHistoricalUsage',
    async (params: { account_id: number; user_ids: number[]; start_date: string; end_date: string }) => {
        try {
            const request = {
                requestBody: {
                    account_id: params.account_id,
                    user_ids: params.user_ids,
                    start_date: params.start_date,
                    end_date: params.end_date
                }
            }
            console.log('履歴使用状況取得API リクエスト:', request)

            const response = await AccountUsersService.getHistoricalUsage(request)
            console.log('履歴使用状況取得API レスポンス:', response)
            return response
        } catch (error: unknown) {
            console.error('履歴使用状況取得API エラー:', error)
            if (error && typeof error === 'object' && 'body' in error) {
                const errorBody = (error as { body: ErrorResponse }).body
                if (errorBody.error_message) {
                    throw new Error(errorBody.error_message)
                }
            }
            throw new Error('通信エラーが発生しました。\n\nインターネット接続をご確認の上、時間をおいて再度お試しください。')
        }
    }
)

const accountUsersSlice = createSlice({
    name: 'accountUsers',
    initialState,
    reducers: {
        clearData: (state) => {
            state.data = null
            state.loading = false
            state.error = null
        },
        setError: (state, action) => {
            state.error = action.payload
        },
        clearError: (state) => {
            state.error = null
        },
    },
    extraReducers: (builder) => {

        // fetchAccountUsers関連
        builder.addCase(fetchAccountUsers.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(fetchAccountUsers.fulfilled, (state, action) => {
            state.data = action.payload
            state.loading = false
            state.error = null
        })
        builder.addCase(fetchAccountUsers.rejected, (state, action) => {
            state.loading = false
            state.error = action.error.message || 'エラーが発生しました'
        })

        // createUser関連
        builder.addCase(createUser.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(createUser.fulfilled, (state, action) => {
            if (state.data) state.data.users.push(action.payload)
            state.loading = false
            state.error = null
        })
        builder.addCase(createUser.rejected, (state, action) => {
            state.loading = false
            state.error = action.error.message || 'エラーが発生しました'
        })

        // updateUser関連
        builder.addCase(updateUser.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(updateUser.fulfilled, (state, action) => {
            if (state.data) {
                // 更新されたユーザー情報を既存のリストに反映
                const index = state.data.users.findIndex(user => user.user_id === action.payload.user_id)
                if (index !== -1) {
                    state.data.users[index] = action.payload
                }
            }
            state.loading = false
            state.error = null
        })
        builder.addCase(updateUser.rejected, (state, action) => {
            state.loading = false
            state.error = action.error.message || 'エラーが発生しました'
        })

        // deleteUser関連
        builder.addCase(deleteUser.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(deleteUser.fulfilled, (state, action) => {
            if (state.data) {
                // 削除されたユーザーをリストから除外
                state.data.users = state.data.users.filter(user => user.user_id !== action.payload.user_id)
            }
            state.loading = false
            state.error = null
        })
        builder.addCase(deleteUser.rejected, (state, action) => {
            state.loading = false
            state.error = action.error.message || 'エラーが発生しました'
        })

        // bulkUpdateUserLimits関連
        builder.addCase(bulkUpdateUserLimits.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(bulkUpdateUserLimits.fulfilled, (state, action) => {
            if (state.data) {
                // 更新されたユーザーのcredit_limitを反映
                const updatedLimitsMap = new Map(
                    action.payload.user_limits.map(ul => [ul.user_id, ul.credit_limit])
                )
                state.data.users.forEach(user => {
                    const newLimit = updatedLimitsMap.get(user.user_id)
                    if (newLimit !== undefined) {
                        user.credit_limit = newLimit
                    }
                })
            }
            state.loading = false
            state.error = null
        })
        builder.addCase(bulkUpdateUserLimits.rejected, (state, action) => {
            state.loading = false
            state.error = action.error.message || 'エラーが発生しました'
        })

        // getHistoricalUsage関連
        builder.addCase(getHistoricalUsage.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(getHistoricalUsage.fulfilled, (state, action) => {
            if (state.data) {
                // 履歴データでユーザー情報を更新
                const historicalDataMap = new Map(
                    action.payload.users.map(u => [u.user_id, u])
                )
                state.data.users.forEach(user => {
                    const historicalData = historicalDataMap.get(user.user_id)
                    if (historicalData) {
                        user.credit_limit = historicalData.credit_limit
                        user.credit_consumed = historicalData.credit_consumed
                    }
                })
            }
            state.loading = false
            state.error = null
        })
        builder.addCase(getHistoricalUsage.rejected, (state, action) => {
            state.loading = false
            state.error = action.error.message || 'エラーが発生しました'
        })

        // bulkValidateUsers関連
        builder.addCase(bulkValidateUsers.pending, (state) => {
            state.loading = true
        })
        builder.addCase(bulkValidateUsers.fulfilled, (state) => {
            state.loading = false
        })
        builder.addCase(bulkValidateUsers.rejected, (state) => {
            state.loading = false
        })

        // bulkCreateUsers関連
        builder.addCase(bulkCreateUsers.fulfilled, (state, action) => {
            if (state.data) {
                // 作成されたユーザーをリストに追加
                state.data.users.push(...action.payload.created_users)
            }
        })
        builder.addCase(bulkCreateUsers.rejected, (state, action) => {
            if (action.payload && state.data && action.payload.created_users.length > 0) {
                state.data.users.push(...action.payload.created_users)
            }
        })
    },
})

const accountUsersActions = {
    ...accountUsersSlice.actions,
    fetchAccountUsers,
    createUser,
    bulkValidateUsers,
    bulkCreateUsers,
    updateUser,
    deleteUser,
    bulkUpdateUserLimits,
    getHistoricalUsage,
}

export { accountUsersActions }
export default accountUsersSlice.reducer